/**
 * CompareSegmentsPicker — the ONE card control of the R8/R9 insight-card grammar.
 *
 * A multi-select checkbox dropdown that ADDS segment-cut rows to the exhibit.
 * "Everyone" is deliberately NOT an option — it is the always-rendered anchor row
 * (Listen-style Total row); the picker only adds cuts under it.
 *
 * Props-driven and shape-agnostic: option groups arrive as
 * `[{ groupLabel, options: [{ id, label, n }] }]`, so Wave 2 can PREPEND an
 * "Audience segments" group (the study's authored subsegments) ahead of the
 * demographic groups with no API change. Past `maxCuts` the remaining checkboxes
 * disable with a "max N for readability" hint — deeper slicing lives in the
 * Report tab.
 */
import { useId, useMemo, useState } from 'react'
import { ChevronDown, SlidersHorizontal } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Text } from '@/components/ui/Text'

export interface CompareSegmentsOption {
  id: string
  label: string
  /** Base size for this cut (unique twins), shown next to the label. */
  n?: number
  /** n=0 authored tag (exhibit pre-applied cut) — shown, not selectable. */
  disabled?: boolean
}

export interface CompareSegmentsGroup {
  groupLabel: string
  options: Array<CompareSegmentsOption>
}

/** A render-only chart-type choice, mirrored into the settings popover's second section.
 *  Single-select (radio) — one active representation at a time. */
export interface ChartTypeOption {
  id: string
  label: string
}

interface CompareSegmentsPickerProps {
  groups: Array<CompareSegmentsGroup>
  /** Selected cut ids (subset of the option ids across groups). */
  selected: Array<string>
  onChange: (ids: Array<string>) => void
  /** Readability cap: further checkboxes disable once this many cuts are checked. */
  maxCuts: number
  /** Trigger appearance — `inline` (labeled "Compare segments" chip, the legacy default)
   *  or `icon` (a small squared toolbar icon + count badge; Wave-7 SEGMENT_CONTROL
   *  option (b), sized/styled to match the PlotCard export-toolbar buttons). Both open
   *  the SAME popover menu; the testids are identical so e2e specs keep passing. */
  variant?: 'inline' | 'icon'
  /** The "Change chart type" section — the render-only representation switch that used to
   *  live as an on-card "View as" pill group. Rendered as a SECOND labeled section inside
   *  this popover ONLY when there are 2+ options; with 0–1 options the whole section (and
   *  its header) is suppressed so a single-view card shows no dead switch. */
  chartTypeOptions?: Array<ChartTypeOption>
  /** The active chart-type id (the backend plot or the live render-only override). */
  selectedChartType?: string
  /** Round-trips a chart-type click back to the card's plot-override state. */
  onSelectChartType?: (id: string) => void
  /** Hover-reveal the (icon-variant) trigger, matching the PlotCard export toolbar: the icon
   *  fades in on card hover and stays hidden at rest. It PINS visible while the popover is open
   *  (so the trigger never vanishes under an open menu — the portaled content can drop card
   *  hover) and while a cut is active (so an active filter stays discoverable). Only the
   *  PlotCard header uses this; the inline report/preview trigger stays always-on. */
  revealOnHover?: boolean
  className?: string
}

export function CompareSegmentsPicker({
  groups,
  selected,
  onChange,
  maxCuts,
  variant = 'inline',
  chartTypeOptions,
  selectedChartType,
  onSelectChartType,
  revealOnHover = false,
  className,
}: CompareSegmentsPickerProps) {
  const [open, setOpen] = useState(false)
  const chartTypeName = useId()
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const atCap = selected.length >= maxCuts
  const hasOptions = groups.some((g) => g.options.length > 0)
  // A single group's label just echoes the "Filter by segment" section header — drop it. Group
  // labels only earn their keep when there are 2+ groups to tell apart (e.g. authored
  // "Audience segments" ahead of demographic cuts).
  const showGroupLabels = groups.filter((g) => g.options.length > 0).length > 1
  // The chart-type section renders ONLY with 2+ options (a lone option is not a switch).
  const showChartTypes = (chartTypeOptions?.length ?? 0) > 1 && Boolean(onSelectChartType)
  // The settings control is reachable whenever there is a real segment filter OR a real
  // chart-type switch to offer — never render an empty popover trigger.
  if (!hasOptions && !showChartTypes) return null

  const toggle = (id: string) => {
    const option = groups.flatMap((g) => g.options).find((o) => o.id === id)
    if (option?.disabled) return
    if (selectedSet.has(id)) {
      onChange(selected.filter((s) => s !== id))
      return
    }
    if (atCap) return
    onChange([...selected, id])
  }

  // The trigger names what it actually opens: a segment filter, a chart-type switch, or both.
  const triggerLabel =
    hasOptions && showChartTypes
      ? 'Chart settings'
      : showChartTypes
        ? 'Change chart type'
        : 'Compare segments'

  const triggerButton =
    variant === 'icon' ? (
      <button
        type="button"
        data-testid="qs-compare-segments-trigger"
        aria-label={triggerLabel}
        title={triggerLabel}
        className={cn(
          'relative inline-flex h-7 w-7 items-center justify-center rounded-md text-twin-chat-fg-muted',
          'transition-colors hover:bg-twin-chat-bubble/40 hover:text-twin-chat-fg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-send/60',
          (open || selected.length > 0) && 'text-twin-chat-fg',
          selected.length > 0 && 'bg-twin-chat-bubble/40',
          // Hover-reveal (PlotCard header only): fade in with the export toolbar on card hover;
          // pin visible while the popover is open or a cut is active so the trigger never
          // disappears under its own menu or hides an in-effect filter.
          revealOnHover &&
            'opacity-0 transition-opacity group-hover/plotcard:opacity-100 focus-visible:opacity-100',
          revealOnHover && (open || selected.length > 0) && 'opacity-100',
          className,
        )}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        {selected.length > 0 && (
          <span
            className="absolute -right-1 -top-1 rounded-full bg-twin-chat-send px-1 text-[10px] font-medium leading-4 text-twin-chat-send-fg"
            data-testid="qs-compare-segments-count"
          >
            {selected.length}
          </span>
        )}
      </button>
    ) : (
      <button
        type="button"
        data-testid="qs-compare-segments-trigger"
        aria-label="Compare segments"
        className={cn(
          'flex items-center gap-1.5 rounded-md border border-twin-chat-divider px-2 py-1 text-xs text-twin-chat-fg-muted transition-colors hover:text-twin-chat-fg',
          selected.length > 0 && 'border-twin-chat-send/60 text-twin-chat-fg',
          className,
        )}
      >
        <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
        Compare segments
        {selected.length > 0 && (
          <span
            className="rounded-full bg-twin-chat-send px-1.5 text-[10px] font-medium leading-4 text-twin-chat-send-fg"
            data-testid="qs-compare-segments-count"
          >
            {selected.length}
          </span>
        )}
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </button>
    )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-80 w-64 space-y-3 overflow-y-auto border-twin-chat-divider bg-twin-chat-surface p-3 text-twin-chat-fg"
        data-testid="qs-compare-segments-menu"
      >
        {hasOptions && (
          <div className="space-y-2" data-testid="qs-compare-segments-section">
            <Text
              as="p"
              size="xs"
              weight="medium"
              className="leading-none text-twin-chat-fg-muted"
            >
              Filter by segment
            </Text>
            {groups.map((g) =>
              g.options.length === 0 ? null : (
                <div key={g.groupLabel} className="space-y-1" data-testid="qs-compare-segments-group">
                  {showGroupLabels && (
                    <p className="text-[11px] font-medium uppercase tracking-wider text-twin-chat-fg-muted">
                      {g.groupLabel}
                    </p>
                  )}
                  {g.options.map((o) => {
                    const checked = selectedSet.has(o.id)
                    const disabled = o.disabled === true || (!checked && atCap)
                    return (
                      <label
                        key={o.id}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-twin-chat-bubble/30',
                          disabled && 'cursor-not-allowed opacity-40',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(o.id)}
                          className="h-3.5 w-3.5 accent-current"
                          data-testid={`qs-compare-segments-option-${o.id}`}
                        />
                        <span className="min-w-0 flex-1 truncate" title={o.label}>
                          {o.label}
                        </span>
                        {typeof o.n === 'number' && (
                          <span className="shrink-0 text-xs tabular-nums text-twin-chat-fg-muted">n={o.n}</span>
                        )}
                      </label>
                    )
                  })}
                </div>
              ),
            )}
            {atCap && (
              <p className="text-[11px] text-twin-chat-fg-muted" data-testid="qs-compare-segments-cap-hint">
                Max {maxCuts} segment{maxCuts === 1 ? '' : 's'} for readability — slice deeper in the Report tab.
              </p>
            )}
          </div>
        )}
        {showChartTypes && (
          <div className="space-y-1" data-testid="qs-chart-type-section">
            <Text
              as="p"
              size="xs"
              weight="medium"
              className="leading-none text-twin-chat-fg-muted"
            >
              Change chart type
            </Text>
            {chartTypeOptions!.map((opt) => (
              <label
                key={opt.id}
                className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-twin-chat-bubble/30"
              >
                <input
                  type="radio"
                  name={chartTypeName}
                  checked={selectedChartType === opt.id}
                  onChange={() => onSelectChartType?.(opt.id)}
                  className="h-3.5 w-3.5 accent-current"
                  data-testid={`qs-chart-type-option-${opt.id}`}
                />
                <span className="min-w-0 flex-1 truncate" title={opt.label}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
